"use client";

import SkeletonImage from "@/components/ui/SkeletonImage";
import ProfileLogoBadge, { LOGO_REVEAL_CLASS } from "@/components/ui/ProfileLogoBadge";
import Link from "@/components/ui/LocaleLink";
import { useTranslations } from "next-intl";
import { MapPin, Globe, Briefcase, EyeSlash } from "@phosphor-icons/react/dist/ssr";
import VerifiedIcon from "@/components/ui/VerifiedIcon";
import { showsVerifiedBadge } from "@/lib/listing-visibility";
import FounderIcon from "@/components/ui/FounderIcon";
import { toLangCodes, langCodesToDisplay } from "@/lib/lang-utils";
import { formatPrice, lowestPaidPrice } from "@/lib/price-utils";
import { formatLocation } from "@/lib/provinces";
import { getCategoryLabel, type DirectoryCategory } from "@/lib/categories";
import { getAvailabilityLabel } from "@/lib/availability-labels";
import { BADGE_TAB_CATEGORY, BADGE_TAB_HIDDEN } from "@/lib/badge-styles";
import {
  LIST_CARD, LIST_CARD_ACTION, LIST_CARD_ACTION_COLUMN, LIST_CARD_BODY, LIST_CARD_LINK,
  LIST_CARD_META, LIST_CARD_META_ITEM, LIST_CARD_NAME, LIST_CARD_SUBTITLE, LIST_CARD_TAB, LIST_CARD_WELL,
  PHOTO_HOVER_ZOOM,
} from "@/lib/card-styles";
import { stripMarkdown } from "@/lib/markdown";
import type { ProfessionalFull } from "@/lib/queries/professionals";
import type { Locale } from "@/lib/locale";

export default function ProfessionalListCard({
  pro,
  locale = "en",
  activeCategory = "",
  activeLocation = "",
}: {
  pro: ProfessionalFull;
  locale?: Locale;
  activeCategory?: string;
  /** Active `?location=` filter slug — flips the location label to matched-city-first. */
  activeLocation?: string;
}) {
  const t = useTranslations("search");
  // When a pro surfaces because of a SECONDARY category (the active filter is
  // one of their secondaries, not their primary), badge the matched category
  // instead of the primary — so the tag reflects WHY they're in these results.
  const badgeCategory =
    activeCategory && activeCategory !== pro.category && pro.secondaryCategories.includes(activeCategory)
      ? activeCategory
      : pro.category;

  // Same principle for location (multi-office): with a location filter
  // active, lead with the MATCHED city — the office that put this pro in
  // these results — not the main office. No filter → main city first.
  // (The profile + grid cards stay main-first; this card mirrors its own
  // matched-category badge behavior.)
  const matchedLocation =
    activeLocation && activeLocation !== pro.location && (pro.provinceSlugs ?? []).includes(activeLocation)
      ? activeLocation
      : pro.location;
  // Count of OTHER provinces the pro has an office in (province_slugs is
  // deduped, so two offices in one province count once — hence "locations",
  // not "offices").
  const extraLocations = (pro.provinceSlugs ?? []).filter((s) => s && s !== matchedLocation).length;

  // Headline is the LOWEST real amount across the pro's packages — not the first
  // one listed. Free packages, hidden prices, and percentage fees are excluded,
  // so a pro with only those reads "Contact for price".
  const lowest = lowestPaidPrice((pro.packages ?? []).map((p) => p.price));
  const formatted = lowest ? formatPrice(lowest) : null;
  const priceFromBlock = formatted ? (<><span className="block text-right text-xs font-normal leading-none text-gray-500">{t("priceFrom")}</span>{formatted}</>) : t("contactForPrice");
  const priceFromInline = formatted ? (<><span className="text-xs font-normal text-gray-500">{t("priceFrom")}</span> {formatted}</>) : t("contactForPrice");
  // Snippet is line-clamped plain text — strip markdown so asterisks etc.
  // never surface in the card UI.
  const description = stripMarkdown(pro.about).split("\n")[0];

  return (
    <article
      aria-label={`${pro.displayName ?? pro.name}, ${pro.title}`}
      className={LIST_CARD}
    >
      <Link
        href={`/professionals/${pro.slug}`}
        className={LIST_CARD_LINK}
      >
        {/* Portrait photo (3:4, the canonical pro-photo ratio).
            TWO boxes, and the split is load-bearing: the OUTER one carries the
            gutter and the positioning context, the INNER one clips. The
            category tab is anchored to the CARD's edge, so it hangs into the
            gutter — inside the clipping well it would simply be cut off at the
            photo's edge, silently. Keep the badge a SIBLING of the well. */}
        <div className={LIST_CARD_WELL}>
          <div className="relative aspect-[3/4] overflow-hidden rounded-control">
            {pro.image && pro.imageType === "logo" ? (
              <ProfileLogoBadge
                src={pro.image}
                alt={`${pro.displayName ?? pro.name} logo`}
                sizes="(min-width: 1024px) 180px, (min-width: 640px) 200px, 104px"
                className="h-full w-full"
                imageClassName={LOGO_REVEAL_CLASS}
              />
            ) : pro.image ? (
              <SkeletonImage
                src={pro.image}
                alt={pro.displayName ?? pro.name}
                fill
                sizes="(min-width: 1024px) 180px, (min-width: 640px) 200px, 104px"
                className={`object-cover ${PHOTO_HOVER_ZOOM}`}
              />
            ) : (
              <div className="h-full w-full bg-navy/5" aria-hidden="true" />
            )}
          </div>
          {/* Flush to the CARD's left edge (hence the negative inset = the
              gutter), square on that edge and rounded on the free one.
              Capped so the tab's right edge lands at 70% ACROSS THE PHOTO and
              a longer label wraps rather than covering the subject's face —
              which is what Vietnamese does ("BẤT ĐỘNG SẢN"). The +8/+13 is the
              gutter the tab spans before it reaches the photo at all.
              `min-w-min` is NOT redundant with the cap: at the 104px mobile
              column, 70% is 62px and a single word cannot wrap, so the cap
              squeezed "Business" 2.17px PAST its own gold background — measured.
              Min-content floors the tab at one whole word; the cap then only
              governs labels that can actually wrap. */}
          <span className={`${BADGE_TAB_CATEGORY} ${LIST_CARD_TAB}`}>
            {getCategoryLabel(badgeCategory as DirectoryCategory, locale)}
          </span>
          {/* Same anchor, same cap: this label is longer than a category in
              every language, and uncapped it reached across the whole photo. */}
          {!pro.visible && (
            <span className={`${BADGE_TAB_HIDDEN} absolute -left-8 top-8 min-w-min max-w-[calc(70%+8px)] leading-tight sm:-left-13 sm:max-w-[calc(70%+13px)]`}>
              <EyeSlash size={10} weight="bold" aria-hidden="true" />
              {t("listingHidden")}
            </span>
          )}
        </div>

        {/* Info */}
        <div className={LIST_CARD_BODY}>
          {/* Meta: single line — location, languages (truncated), experience */}
          <p className={LIST_CARD_META}>
            <span className={LIST_CARD_META_ITEM}>
              <MapPin aria-hidden="true" weight="bold" className="w-13 h-13 shrink-0" />
              {formatLocation(matchedLocation, locale)}
              {extraLocations > 0 && (
                /* Below `sm` the full clause ("· +2 locations") ran past the card's own
                   edge and was clipped mid-word, so the count shows as "(+2)" there and
                   the clause returns from `sm` up, which is where the card also gains its
                   wider padding and a larger meta type. Decided in CSS, not from a media
                   query read in JS, so the server and the client render the same markup.
                   Both visual forms are hidden from assistive tech and the full clause is
                   read instead, at every width. */
                <span>
                  <span aria-hidden="true" className="sm:hidden">{t("nMoreLocationsShort", { n: extraLocations })}</span>
                  <span aria-hidden="true" className="hidden sm:inline"><span className="mx-3">·</span>{t("nMoreLocations", { n: extraLocations })}</span>
                  <span className="sr-only">{t("nMoreLocations", { n: extraLocations })}</span>
                </span>
              )}
              {(pro.languages || pro.yearsExperience > 0) && <span aria-hidden="true" className="ml-2">·</span>}
            </span>
            {pro.languages && (() => {
              const langCount = pro.languages.split(",").length;
              const useCodes = langCount > 3;
              // Prefer locale-aware codes; fall back to the legacy English
              // string for back-compat (always passed by the query layer).
              const display = pro.languageCodes
                ? langCodesToDisplay(pro.languageCodes, locale)
                : pro.languages;
              return (
                <span className="shrink-0" aria-label={display} title={display}>
                  <Globe aria-hidden="true" weight="bold" className="inline-block w-13 h-13 shrink-0 align-[-1px]" />
                  <span className={useCodes ? "" : "sm:hidden"}>&nbsp;{toLangCodes(pro.languageCodes ?? pro.languages)}</span>
                  {!useCodes && <span className="hidden sm:inline">&nbsp;{display}</span>}
                </span>
              );
            })()}
            {pro.yearsExperience > 0 && (
              <span className={`meta-experience ${LIST_CARD_META_ITEM}`}>
                <span aria-hidden="true" className="meta-experience-dot">·</span>
                <Briefcase aria-hidden="true" weight="bold" className="w-13 h-13 shrink-0" />
                {pro.yearsExperience}+<span className="hidden sm:inline">&nbsp;{t("yearsSuffix")}</span>
              </span>
            )}
          </p>

          {/* Name + founder/verified badges */}
          <h2 className={LIST_CARD_NAME}>
            {pro.displayName ?? pro.name}
            {pro.founder && <FounderIcon size={16} nudge={2} />}
            {showsVerifiedBadge(pro) && <VerifiedIcon size={16} nudge={2} />}
          </h2>
          <p className={LIST_CARD_SUBTITLE}>{pro.title}</p>

          {/* Availability */}
          {pro.availability && (
            <p className={[
              "mt-5 text-[10px] font-medium sm:text-[11px]",
              pro.availability === "Available now — accepting new clients"
                ? "text-green"
                : pro.availability === "Fully booked — waitlist only"
                  ? "text-amber"
                  : "text-gray-500",
            ].join(" ")}>
              ●&nbsp;{getAvailabilityLabel(pro.availability, locale)}
            </p>
          )}

          {/* Description — 4 lines + ellipsis on sm+ (hidden on mobile). Use
              sm:line-clamp-4 (which sets display:-webkit-box); a plain sm:block
              would override that display and silently defeat the clamp. */}
          <p className="mt-13 hidden text-sm leading-relaxed text-gray-600 sm:line-clamp-4">
            {description}
          </p>

          {/* Price + rating — visible on mobile/tablet, hidden at lg where the third column takes over */}
          <div className="mt-8 flex flex-wrap items-center gap-x-13 gap-y-3 lg:hidden">
            {/* "from" shows at every width: the number is the LOWEST of the
                pro's packages, so a bare amount reads as the price rather than
                a starting point. */}
            <span className="text-sm font-medium text-navy">{priceFromInline}</span>
            {pro.rating != null && (
              <span className="whitespace-nowrap text-xs text-gray-600">
                <span className="text-gold" aria-hidden="true">★</span>{" "}
                {pro.rating} ({pro.reviewCount})
              </span>
            )}
          </div>
        </div>

        {/* Action: rating + price at top, CTA at bottom */}
        <div className={LIST_CARD_ACTION_COLUMN}>
          <div>
            {pro.rating != null && (
              <p className="text-sm font-medium text-navy">
                <span className="text-gold" aria-hidden="true">★</span>{" "}
                <span className="sr-only">{t("ratingSr")} </span>
                {pro.rating}{" "}
                <span className="text-xs text-gray-500">({pro.reviewCount})</span>
              </p>
            )}
            {/* A real amount headlines at price size. "Contact for price" is a
                label, not an amount: at 1.4rem it wrapped onto two lines and
                outweighed the name (owner, 2026-09-13), so it takes the package
                card's label style instead, on one line. */}
            <p
              className={`mt-13 text-navy ${
                formatted ? "text-[1.4rem] font-medium leading-tight" : "whitespace-nowrap text-sm font-semibold"
              }`}
            >
              {priceFromBlock}
            </p>
          </div>
          <span className={LIST_CARD_ACTION}>
            {t("viewProfile")}
          </span>
        </div>
      </Link>
    </article>
  );
}
